import React, { useState, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import TonuModal from '../../../../modals/tonu-modal';
import { CustomIcon } from '../../../../UI';
import './StatusSection.scss';

const StatusSection = () => {
  const { register, formState: { errors }, watch, setValue } = useFormContext();
  const customerRate = watch('customerRate');
  const carrierRate = watch('carrierRate');
  const status = watch('status');
  const isCancelled = status === 'Cancelled';
  const tonu = watch('tonu') || { enabled: false, carrierRate: '', customerRate: '' };
  
  // Fee fields
  const fees = watch('fees') || [];
  
  // Calculate total rates including fees
  const calculateTotalCustomerRate = () => {
    const baseRate = parseFloat(customerRate) || 0;
    const feesTotal = fees.reduce((sum, fee) => {
      return sum + (parseFloat(fee.customerRate) || 0);
    }, 0);
    return baseRate + feesTotal;
  };

  const calculateTotalCarrierRate = () => {
    const baseRate = parseFloat(carrierRate) || 0;
    const feesTotal = fees.reduce((sum, fee) => {
      return sum + (parseFloat(fee.carrierRate) || 0);
    }, 0);
    return baseRate + feesTotal;
  };

  const totalCustomerRate = calculateTotalCustomerRate();
  const totalCarrierRate = calculateTotalCarrierRate();
  
  // TONU modal state
  const [showTonuModal, setShowTonuModal] = useState(false);
  const previousStatusRef = useRef(status);
  
  // Check if we should show fee fields
  // Show fees always - they can be added at any time
  const showFeeFields = true;
  
  // Watch for status changes to show TONU modal
  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    const isTonuStatus = status === 'On Hold' || status === 'Cancelled';
    const wasTonuStatus = previousStatus === 'On Hold' || previousStatus === 'Cancelled';
    
    // Show modal if status changed to onHold or canceled (only if previous status exists and is different)
    if (isTonuStatus && !wasTonuStatus && previousStatus && previousStatus !== status) {
      setShowTonuModal(true);
    }
    
    previousStatusRef.current = status;
  }, [status]);

  const addFee = () => {
    const newFees = [...fees, { type: 'Detention', carrierRate: '', customerRate: '' }];
    setValue('fees', newFees, { shouldDirty: true });
  };

  const removeFee = (index) => {
    const newFees = fees.filter((_, i) => i !== index);
    setValue('fees', newFees, { shouldDirty: true });
  };

  const updateFee = (index, field, value) => {
    const newFees = [...fees];
    newFees[index] = { ...newFees[index], [field]: value };
    setValue('fees', newFees, { shouldDirty: true });
  };

  const handleTonuSave = (tonuData) => {
    setValue('tonu', tonuData, { shouldDirty: true });
    // TONU rates are stored separately in tonu object
    // They don't replace base rates, they are additional charges
    // The base rates remain unchanged
  };

  return (
    <div className="status-section">
      <h3 className="form-section-title">Status</h3>
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            {...register('status')}
            className={errors.status ? 'error' : ''}
            disabled={isCancelled}
          >
            <option value="Listed">Listed</option>
            <option value="Dispatched">Dispatched</option>
            <option value="Picked Up">Picked Up</option>
            <option value="Delivered">Delivered</option>
            <option value="Cancelled">Cancelled</option>
            <option value="On Hold">On Hold</option>
          </select>
          {errors.status && (
            <span className="error-message">{errors.status.message}</span>
          )}
          {isCancelled && (
            <span className="error-message">Cancelled loads cannot be modified.</span>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="tracking">Tracking Link</label>
          <input
            type="url"
            id="tracking"
            placeholder="https://"
            {...register('tracking')}
            className={errors.tracking ? 'error' : ''}
          />
          {errors.tracking && (
            <span className="error-message">{errors.tracking.message}</span>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="customerRate">
            Customer Rate ($)
            {showFeeFields && fees.length > 0 && (
              <span className="rate-info">
                <span className="base-rate">Base: ${(parseFloat(customerRate) || 0).toFixed(2)}</span>
                <span className="total-rate">Total: ${totalCustomerRate.toFixed(2)}</span>
              </span>
            )}
          </label>
          <input
            type="number"
            id="customerRate"
            value={customerRate || ''}
            readOnly
            disabled
            className="read-only-field"
            onWheel={(e) => e.target.blur()}
          />
        </div>
        <div className="form-group">
          <label htmlFor="carrierRate">
            Carrier rate ($)
            {showFeeFields && fees.length > 0 && (
              <span className="rate-info">
                <span className="base-rate">Base: ${(parseFloat(carrierRate) || 0).toFixed(2)}</span>
                <span className="total-rate">Total: ${totalCarrierRate.toFixed(2)}</span>
              </span>
            )}
          </label>
          <input
            type="number"
            id="carrierRate"
            placeholder="Rate"
            {...register('carrierRate')}
            className={errors.carrierRate ? 'error' : ''}
            onWheel={(e) => e.target.blur()}
          />
          {errors.carrierRate && (
            <span className="error-message">{errors.carrierRate.message}</span>
          )}
        </div>
      </div>

      {/* Fee Fields for Freight Loads */}
      {showFeeFields && (
        <div className="fees-section">
          <div className="fees-header">
            <h4 className="form-section-subtitle">Additional Fees</h4>
            <button
              type="button"
              onClick={addFee}
              className="add-fee-btn"
            >
              <CustomIcon name="plus-01" size={16} type="svg" />
              Add Fee
            </button>
          </div>

          {fees.map((fee, index) => (
            <div key={index} className="fee-item">
              <div className="form-group">
                <label htmlFor={`fee-type-${index}`}>Fee Type</label>
                <select
                  id={`fee-type-${index}`}
                  value={fee.type || 'Detention'}
                  onChange={(e) => updateFee(index, 'type', e.target.value)}
                >
                  <option value="Detention">Detention</option>
                  <option value="Layover">Layover</option>
                  <option value="Lumper fee">Lumper fee</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor={`fee-customer-${index}`}>Customer Rate Addition ($)</label>
                <input
                  type="number"
                  id={`fee-customer-${index}`}
                  placeholder="0.00"
                  value={fee.customerRate || ''}
                  onChange={(e) => updateFee(index, 'customerRate', e.target.value)}
                  onWheel={(e) => e.target.blur()}
                />
              </div>
              <div className="form-group">
                <label htmlFor={`fee-carrier-${index}`}>Carrier Rate Addition ($)</label>
                <input
                  type="number"
                  id={`fee-carrier-${index}`}
                  placeholder="0.00"
                  value={fee.carrierRate || ''}
                  onChange={(e) => updateFee(index, 'carrierRate', e.target.value)}
                  onWheel={(e) => e.target.blur()}
                />
              </div>
              <button
                type="button"
                onClick={() => removeFee(index)}
                className="fee-remove-btn"
                aria-label="Remove fee"
              >
                <CustomIcon name="close-small" size={16} type="svg" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* TONU Modal */}
      <TonuModal
        isOpen={showTonuModal}
        onClose={() => setShowTonuModal(false)}
        onSave={handleTonuSave}
        initialCarrierRate={carrierRate || ''}
        initialCustomerRate={customerRate || ''}
      />
    </div>
  );
};

export default StatusSection;

